import React, {
  createContext,
  useContext,
  useRef,
  useState,
  useEffect,
  useCallback,
} from "react";
import { useI18n } from "@/i18n";

/**
 * AI-powered auto-translation (Emergent LLM via /api/translate).
 * The site is authored in TURKISH (source). When EN/ES is selected, every
 * registered string is batch-translated and cached in localStorage.
 * TR renders instantly with no network.
 */

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CACHE_KEY = "milkos_tr_cache_v3";
const SOURCE = "tr";

function loadCache() {
  try {
    return JSON.parse(localStorage.getItem(CACHE_KEY)) || {};
  } catch (e) {
    return {};
  }
}

const Ctx = createContext({ tr: (t) => t });

export function AutoTranslateProvider({ children }) {
  const { lang } = useI18n();
  const cacheRef = useRef(loadCache());
  const allRef = useRef(new Set());
  const pendingRef = useRef(new Set());
  const timerRef = useRef(null);
  const [, force] = useState(0);
  const langRef = useRef(lang);
  langRef.current = lang;

  const persist = () => {
    try {
      localStorage.setItem(CACHE_KEY, JSON.stringify(cacheRef.current));
    } catch (e) {
      /* noop */
    }
  };

  const flush = useCallback(async () => {
    const l = langRef.current;
    if (l === SOURCE) return;
    const pend = Array.from(pendingRef.current);
    pendingRef.current = new Set();
    const langCache = cacheRef.current[l] || (cacheRef.current[l] = {});
    const missing = pend.filter((t) => !(t in langCache));
    if (missing.length === 0) return;
    try {
      const res = await fetch(`${API}/translate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ texts: missing, target: l, source: SOURCE }),
      });
      const data = await res.json();
      Object.assign(langCache, data.translations || {});
      persist();
      force((v) => v + 1);
    } catch (e) {
      /* offline / error → keep source */
    }
  }, []);

  const schedule = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(flush, 220);
  }, [flush]);

  const register = useCallback(
    (text) => {
      if (!text || typeof text !== "string") return;
      allRef.current.add(text);
      const l = langRef.current;
      if (l === SOURCE) return;
      const langCache = cacheRef.current[l];
      if (langCache && text in langCache) return;
      pendingRef.current.add(text);
      schedule();
    },
    [schedule],
  );

  const tr = useCallback(
    (text) => {
      if (!text || typeof text !== "string") return text;
      register(text);
      const l = langRef.current;
      if (l === SOURCE) return text;
      const langCache = cacheRef.current[l];
      return (langCache && langCache[text]) || text;
    },
    [register],
  );

  useEffect(() => {
    if (lang !== SOURCE) {
      const langCache = cacheRef.current[lang] || (cacheRef.current[lang] = {});
      allRef.current.forEach((t) => {
        if (!(t in langCache)) pendingRef.current.add(t);
      });
      schedule();
    }
    force((v) => v + 1);
  }, [lang, schedule]);

  return <Ctx.Provider value={{ tr }}>{children}</Ctx.Provider>;
}

export function useTr() {
  return useContext(Ctx).tr;
}

export function T({ children }) {
  const tr = useTr();
  const text =
    typeof children === "string"
      ? children
      : Array.isArray(children)
        ? children.join("")
        : String(children ?? "");
  return <>{tr(text)}</>;
}
